import {
  type ChangeEvent,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useMemo,
} from "react";
import { CloseIcon, DownloadIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  downloadBlobAsFile,
  downloadUrlAsFile,
} from "@vega-core/utils/download.helpers";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { PDFViewerPageNavigation } from "./PDFViewerPageNavigation";
import { PDFViewerZoomControls } from "./PDFViewerZoomControls";

export type PDFViewerPanelProps = {
  scrollToPage: (pageNumber: number) => void;
  scale: number;
  totalDocumentPages: number;
  currentDocument?: string;
  currentDocumentPage: number;
  setCurrentDocumentPage: Dispatch<SetStateAction<number>>;
  setScale: Dispatch<SetStateAction<number>>;
  documentsDropdown?: ReactNode;
  blobOrString?: Blob | string;
  isLoading: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  isDownloadable?: boolean;
  isDownloadEnabled?: boolean;
  zoomLevels: number[];
  isScrollable: boolean;
  headerActions?: ReactNode;
  onDownload?: (documentId: string) => Promise<void>;
  embedded?: boolean;
  currentDocumentName?: string;
};

export const PDFViewerPanel = ({
  scrollToPage,
  scale,
  setScale,
  totalDocumentPages,
  currentDocument,
  currentDocumentPage,
  setCurrentDocumentPage,
  documentsDropdown,
  blobOrString,
  isLoading,
  setIsOpen,
  isDownloadable = true,
  isDownloadEnabled = true,
  zoomLevels,
  isScrollable,
  headerActions,
  onDownload,
  embedded = false,
  currentDocumentName,
}: PDFViewerPanelProps) => {
  const isFirstPage = currentDocumentPage === 1;
  const isLastPage = currentDocumentPage === totalDocumentPages;
  const documentZoomOptions = useMemo(
    () =>
      zoomLevels.map((option) => ({
        label: `${option}%`,
        value: String(option),
      })),
    [zoomLevels],
  );

  const goToPreviousPage = () => {
    if (!isFirstPage) {
      setCurrentDocumentPage(currentDocumentPage - 1);

      if (isScrollable) {
        scrollToPage(currentDocumentPage - 1);
      }
    }
  };

  const goToNextPage = () => {
    if (!isLastPage) {
      setCurrentDocumentPage(currentDocumentPage + 1);

      if (isScrollable) {
        scrollToPage(currentDocumentPage + 1);
      }
    }
  };

  const onPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const newcurrentDocumentPage = Number(value);

    if (
      newcurrentDocumentPage < 1 ||
      newcurrentDocumentPage > totalDocumentPages
    ) {
      return;
    }

    setCurrentDocumentPage(newcurrentDocumentPage);

    if (isScrollable) {
      scrollToPage(newcurrentDocumentPage);
    }
  };

  const changeZoomLevel = (zoom: "IN" | "OUT") => {
    const currentScalePercent = Math.round(scale * 100);

    // Find exact match or closest zoom level
    let currentZoomIndex = documentZoomOptions.findIndex(
      (option) => Number(option.value) === currentScalePercent,
    );

    // If no exact match, find the closest one
    if (currentZoomIndex === -1) {
      currentZoomIndex = documentZoomOptions.reduce(
        (closestIdx, option, idx) => {
          const currentDiff = Math.abs(
            Number(option.value) - currentScalePercent,
          );
          const closestDiff = Math.abs(
            Number(documentZoomOptions[closestIdx].value) - currentScalePercent,
          );

          return currentDiff < closestDiff ? idx : closestIdx;
        },
        0,
      );
    }

    if (zoom === "OUT") {
      const targetIndex = currentZoomIndex - 1;

      if (!isTruthy(documentZoomOptions[targetIndex])) {
        return;
      }

      setScale(Number(documentZoomOptions[targetIndex].value) / 100);
    }

    if (zoom === "IN") {
      const targetIndex = currentZoomIndex + 1;

      if (!isTruthy(documentZoomOptions[targetIndex])) {
        return;
      }

      setScale(Number(documentZoomOptions[targetIndex].value) / 100);
    }
  };

  const downloadFile = async () => {
    if (onDownload) {
      if (!currentDocument) return;

      try {
        await onDownload(currentDocument);
      } catch (error) {
        console.error("Error downloading document:", error);
      }
    } else if (isTruthy(blobOrString)) {
      const fileName = currentDocumentName || currentDocument || "document";

      if (typeof blobOrString === "string") {
        await downloadUrlAsFile(blobOrString, fileName);
      } else {
        downloadBlobAsFile(blobOrString, fileName);
      }
    }
  };

  const getDownloadButton = () => {
    if (!isTruthy(blobOrString) || !isDownloadEnabled) return null;

    return documentsDropdown ? (
      <ThemedIconButton
        ariaLabel="download file"
        dataTestId="pdf-download-file"
        disabled={!isDownloadable}
        icon={<DownloadIcon />}
        isLoading={isLoading}
        onClick={downloadFile}
        size="large"
      />
    ) : (
      <ThemedButton
        dataTestId="download-file"
        icon={<DownloadIcon />}
        onClick={downloadFile}
        variant="secondary"
      >
        {m.common_download()}
      </ThemedButton>
    );
  };

  const getMobileControls = () => (
    <div className="md:hidden">
      {!embedded && (
        <div className="h-40 flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between">
            <div className="body-extra-large-semibold text-text/primary">
              {m.common_document()}
            </div>
            <ThemedIconButton
              ariaLabel="select a file"
              dataTestId="pdf-close-mobile"
              icon={<CloseIcon />}
              onClick={() => setIsOpen?.(false)}
              variant="noBackground"
            />
          </div>
          <div
            className={cnTwMerge({
              "flex items-center justify-between gap-3":
                isTruthy(documentsDropdown),
            })}
          >
            <div
              className={cnTwMerge({
                "flex-1": isTruthy(documentsDropdown),
                "skeleton-box": isLoading,
              })}
            >
              {documentsDropdown}
            </div>
            {getDownloadButton()}
          </div>
        </div>
      )}
      <div
        className={cnTwMerge(
          "min-h-20 border-b-1 border-border-primary pt-4 pb-4 pl-4 pr-4 flex items-center justify-between gap-4",
          {
            "border-t-1": !embedded,
          },
        )}
      >
        <PDFViewerZoomControls
          changeZoomLevel={changeZoomLevel}
          documentZoomOptions={documentZoomOptions}
          isMobile
          scale={scale}
          setScale={setScale}
        />
        <PDFViewerPageNavigation
          currentDocumentPage={currentDocumentPage}
          goToNextPage={goToNextPage}
          goToPreviousPage={goToPreviousPage}
          isLastPage={isLastPage}
          isLoading={isLoading}
          onPageChange={onPageChange}
          totalDocumentPages={totalDocumentPages}
        />
      </div>
    </div>
  );

  const getDesktopControls = () => (
    <div className="hidden md:block">
      {!embedded && (
        <div className="h-20 p-4 flex items-center justify-between">
          <div
            className={cnTwMerge("flex items-center h-full w-full gap-4", {
              "skeleton-box": isLoading,
            })}
          >
            <ThemedIconButton
              ariaLabel="select a file"
              dataTestId="pdf-close-desktop"
              icon={<CloseIcon />}
              onClick={() => setIsOpen?.(false)}
              variant="noBackground"
            />
            <div className="border-r-1 h-full" />
            {headerActions ? getDownloadButton() : null}
            {documentsDropdown}
            <div
              className={cnTwMerge("flex items-center gap-2", {
                "w-full flex justify-end": isFalsy(documentsDropdown),
              })}
            >
              {headerActions ? headerActions : getDownloadButton()}
            </div>
          </div>
        </div>
      )}
      <div
        className={cnTwMerge(
          "h-20 border-b-1 border-border-primary pt-4 pb-4 pl-6 pr-6 flex items-center justify-between gap-2",
          {
            "border-t-1": !embedded,
          },
        )}
      >
        <div
          className={cnTwMerge("flex gap-2", {
            "skeleton-box": isLoading,
          })}
        >
          <PDFViewerZoomControls
            changeZoomLevel={changeZoomLevel}
            documentZoomOptions={documentZoomOptions}
            isMobile={false}
            scale={scale}
            setScale={setScale}
          />
        </div>
        <PDFViewerPageNavigation
          currentDocumentPage={currentDocumentPage}
          goToNextPage={goToNextPage}
          goToPreviousPage={goToPreviousPage}
          isLastPage={isLastPage}
          isLoading={isLoading}
          onPageChange={onPageChange}
          totalDocumentPages={totalDocumentPages}
        />
      </div>
    </div>
  );

  return (
    <>
      {getMobileControls()}
      {getDesktopControls()}
    </>
  );
};
